import { useState } from "react";

import FallbackImage from "newui/components/FallbackImage/FallbackImage";
import SkeletonBase from "newui/components/Skeleton";
import ClickExpandTextModal from "newui/components/ClickExpandTextModal";

import type { Company } from "newui/features/Company/types";
import { ServiceabilityLocationDisplay } from "../../../CompanyServiceability";
import { CompanyContactDetails } from "./CompanyContactDetails";
import { CompanyDescriptionExpand } from "./CompanyDescriptionExpand";

interface CompanySidebarProps {
	company: Company;
	isLoading: boolean;
}

export const CompanySidebar: React.FC<CompanySidebarProps> = ({
	company,
	isLoading,
}) => {
	const globalPresence = company.global_presence?.map((g) => g.name) ?? [];
	const primaryAddress = company.locations?.[0]?.address;

	if (isLoading) {
		return (
			<div className="hidden w-[350px] shrink-0 flex-col gap-4 border-r border-gray-200 p-6 lg:flex xl:w-[400px]">
				<SkeletonBase width="80px" height="80px" />
				<SkeletonBase width="200px" height="24px" />
				<SkeletonBase width="100%" height="48px" />
				<SkeletonBase width="100%" height="80px" />
			</div>
		);
	}

	return (
		<aside className="hidden w-[350px] shrink-0 flex-col border-r border-gray-200 lg:flex xl:w-[400px]">
			<div className="sticky top-0 flex flex-col gap-4 overflow-y-auto p-6">
				{/* Logo */}
				<div className="h-20 w-20 overflow-hidden rounded-lg border border-gray-100">
					<FallbackImage
						src={company.logo_path}
						alt={company.name}
						className="h-full w-full object-contain"
					/>
				</div>

				{/* Name */}
				<h1 className="font-roboto text-xl font-semibold text-gray-900">
					{company.name}
				</h1>

				{/* Description */}
				<CompanyDescriptionExpand
					company={company}
				/>

				{/* Company Metadata */}
				{(company.years_trading || globalPresence.length > 0) && (
					<div className="flex flex-col gap-1 border-t border-gray-100 pt-4">
						<h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
							Company Information
						</h3>
						{company.years_trading && (
							<p className="text-sm text-gray-600">
								Years of Trading:{" "}
								<span className="font-medium text-gray-900">
									{company.years_trading}
								</span>
							</p>
						)}
						{globalPresence.length > 0 && (
							<p className="text-sm text-gray-600">
								Global Presence:{" "}
								<span className="font-medium text-gray-900">
									{globalPresence.join(", ")}
								</span>
							</p>
						)}
						<ServiceabilityLocationDisplay value={company.serviceability_location_ids} showCode />
					</div>
				)}

				{/* Contact Details */}
				<CompanyContactDetails
					className="border-t border-gray-100 pt-4"
					phone={company.phone}
					email={company.email}
					primaryAddress={primaryAddress}
					website={company.website}
				/>
			</div>
		</aside>
	);
};
